import type { DevicePlanResponse, DeviceSettings, RadioChannel } from "@somewear/api";
import {
	DeviceActivationStateType,
	DeviceRecord,
	RadioBandwidth,
	RadioSpreadFactor,
} from "@somewear/api";
import { DeviceActivationStateTypeMap } from "@somewear/api/src/proto/api/device_pb";
import type { IQueuedDeviceActivationChange } from "@somewear/model";
import { DeviceActivationChange, TrackingIntervalIds } from "@somewear/model";
import { IOrganizationWorkspaceSettingsState } from "@somewear/organization";
import { IWorkspaceSettingsState, WorkspaceDefaultsFieldsSchema } from "@somewear/workspace";

import {
	IntervalCategory,
	NetworkType,
	SelectOption,
	SplitIntervalValues,
	toTrackingIntervalSelectOption,
	TrackingInterval,
	trackingIntervals,
} from "./settings.model";

export interface DeviceActivationStateOptions {
	id: DeviceActivationChange;
	name: string;
	target: DeviceActivationStateTypeMap[keyof DeviceActivationStateTypeMap];
}

export function getDeviceActivationStates(
	currentActivationState?: DevicePlanResponse.AsObject["activationState"],
	pendingActivationState?: DevicePlanResponse.AsObject["activationState"],
	gateway?: DeviceRecord.GatewayMap[keyof DeviceRecord.GatewayMap],
): DeviceActivationStateOptions[] {
	if (gateway === DeviceRecord.Gateway.INMARSAT) {
		const isPendingActivation =
			pendingActivationState === DeviceActivationStateType.STATUS_ACTIVE;
		const isPendingDeactivation =
			pendingActivationState === DeviceActivationStateType.STATUS_DISABLED;

		if (currentActivationState === DeviceActivationStateType.STATUS_ACTIVE) {
			if (isPendingDeactivation) {
				return [
					{
						id: DeviceActivationChange.DISABLE,
						name: "Deactivate",
						target: DeviceActivationStateType.STATUS_DISABLED,
					},
					{
						id: DeviceActivationChange.NONE,
						name: "Revert to Active",
						target: currentActivationState,
					},
				];
			}

			return [
				{
					id: DeviceActivationChange.NONE,
					name: "Active",
					target: DeviceActivationStateType.STATUS_ACTIVE,
				},
				{
					id: DeviceActivationChange.DISABLE,
					name: "Deactivate device",
					target: DeviceActivationStateType.STATUS_DISABLED,
				},
			];
		} else if (currentActivationState === DeviceActivationStateType.STATUS_DISABLED) {
			if (isPendingActivation) {
				return [
					{
						id: DeviceActivationChange.ACTIVATE,
						name: "Activate",
						target: DeviceActivationStateType.STATUS_DISABLED,
					},
					{
						id: DeviceActivationChange.NONE,
						name: "Revert to Deactivated",
						target: currentActivationState,
					},
				];
			}

			return [
				{
					id: DeviceActivationChange.NONE,
					name: "Active",
					target: DeviceActivationStateType.STATUS_ACTIVE,
				},
				{
					id: DeviceActivationChange.NONE,
					name: "Revert to Disabled",
					target: DeviceActivationStateType.STATUS_DISABLED,
				},
			];
		}
	}

	const isPendingSuspended =
		pendingActivationState === DeviceActivationStateType.STATUS_SUSPENDING;
	const isPendingActive = pendingActivationState === DeviceActivationStateType.STATUS_ACTIVE;

	if (currentActivationState === DeviceActivationStateType.STATUS_ACTIVE) {
		if (isPendingSuspended) {
			return [
				{
					id: DeviceActivationChange.SUSPEND,
					name: "Suspended",
					target: DeviceActivationStateType.STATUS_SUSPENDING,
				},
				{
					id: DeviceActivationChange.NONE,
					name: "Revert to Active",
					target: currentActivationState,
				},
			];
		}

		return [
			{
				id: DeviceActivationChange.NONE,
				name: "Active",
				target: DeviceActivationStateType.STATUS_ACTIVE,
			},
			{
				id: DeviceActivationChange.SUSPEND,
				name: "Suspend subscription",
				target: DeviceActivationStateType.STATUS_SUSPENDING,
			},
		];
	} else if (currentActivationState === DeviceActivationStateType.STATUS_DISABLED) {
		if (isPendingActive) {
			return [
				{
					id: DeviceActivationChange.SUSPEND,
					name: "Suspended",
					target: DeviceActivationStateType.STATUS_SUSPENDING,
				},
				{
					id: DeviceActivationChange.NONE,
					name: "Revert to Disabled",
					target: currentActivationState,
				},
			];
		}

		return [
			{
				id: DeviceActivationChange.NONE,
				name: "Disabled",
				target: DeviceActivationStateType.STATUS_DISABLED,
			},
			{
				id: DeviceActivationChange.SUSPEND,
				name: "Suspend subscription",
				target: DeviceActivationStateType.STATUS_SUSPENDING,
			},
		];
	} else if (
		currentActivationState === DeviceActivationStateType.STATUS_SUSPENDED ||
		currentActivationState === DeviceActivationStateType.STATUS_SUSPENDING
	) {
		if (isPendingActive) {
			return [
				{
					id: DeviceActivationChange.ACTIVATE,
					name: "Active",
					target: DeviceActivationStateType.STATUS_ACTIVE,
				},
				{
					id: DeviceActivationChange.NONE,
					name: "Revert to Suspended",
					target: currentActivationState,
				},
			];
		}

		return [
			{
				id: DeviceActivationChange.NONE,
				name: "Suspended",
				target: DeviceActivationStateType.STATUS_SUSPENDED,
			},
			{
				id: DeviceActivationChange.ACTIVATE,
				name: "Resume subscription",
				target: DeviceActivationStateType.STATUS_ACTIVE,
			},
		];
	} else if (currentActivationState === DeviceActivationStateType.STATUS_INACTIVE) {
		if (isPendingActive) {
			return [
				{
					id: DeviceActivationChange.ACTIVATE,
					name: "Active",
					target: DeviceActivationStateType.STATUS_ACTIVE,
				},
				{
					id: DeviceActivationChange.NONE,
					name: "Revert to Deactivated",
					target: currentActivationState,
				},
			];
		}

		return [
			{
				id: DeviceActivationChange.NONE,
				name: "Deactivated",
				target: DeviceActivationStateType.STATUS_INACTIVE,
			},
			{
				id: DeviceActivationChange.ACTIVATE,
				name: "Activate device",
				target: DeviceActivationStateType.STATUS_ACTIVE,
			},
		];
	}

	if (currentActivationState === DeviceActivationStateType.STATUS_ERROR) {
		return [
			{
				id: DeviceActivationChange.NONE,
				name: "Error",
				target: DeviceActivationStateType.STATUS_ERROR,
			},
		];
	}

	return [
		{
			id: DeviceActivationStateType.STATUS_NONE,
			name: "Unknown",
			target: DeviceActivationStateType.STATUS_NONE,
		},
	];
}

export function isActivationChangeSignificant(
	changeCommand: IQueuedDeviceActivationChange,
): boolean {
	if (changeCommand.change === DeviceActivationChange.NONE) return false;

	if (
		changeCommand.currentState === DeviceActivationStateType.STATUS_SUSPENDING ||
		changeCommand.currentState === DeviceActivationStateType.STATUS_SUSPENDED
	) {
		return changeCommand.change !== DeviceActivationChange.SUSPEND;
	}

	if (changeCommand.currentState === DeviceActivationStateType.STATUS_ACTIVE) {
		return changeCommand.change !== DeviceActivationChange.ACTIVATE;
	}

	return true;
}

export const satTrackingIntervals = trackingIntervals.filter((t) =>
	t.networkTypes?.includes(NetworkType.Satellite),
);
export const meshTrackingIntervals = trackingIntervals.filter((t) =>
	t.networkTypes?.includes(NetworkType.Mesh),
);

export function getSatelliteTrackingIntervalOptions(
	meshIntervalId?: TrackingIntervalIds,
): SelectOption<TrackingInterval>[] {
	if (!meshIntervalId) {
		return satTrackingIntervals.map((t) => ({
			...toTrackingIntervalSelectOption(t),
			disabled: true,
		}));
	}

	const meshIntervalVal = trackingIntervals.find((t) => t.id === meshIntervalId);

	if (!meshIntervalVal) {
		throw new Error(`Mesh interval with id ${meshIntervalId} not found`);
	}

	return satTrackingIntervals.map((t) => ({
		...toTrackingIntervalSelectOption(t),
		disabled: t.shareRate < meshIntervalVal.shareRate || t.logRate < meshIntervalVal.logRate,
	}));
}

export function getCompatibleSatTrackingInterval(
	meshInterval: Pick<TrackingInterval, "shareRate" | "logRate">,
	candidateSatInterval: TrackingInterval,
) {
	if (
		candidateSatInterval.shareRate < meshInterval.shareRate ||
		candidateSatInterval.logRate < meshInterval.logRate
	) {
		const suitableSatInterval = satTrackingIntervals.find(
			(t) => t.shareRate >= meshInterval.shareRate && t.logRate >= meshInterval.logRate,
		);

		if (!suitableSatInterval) {
			throw new Error(
				`Satellite Interval with share rate greater or equal to share rate: ${meshInterval.shareRate} and log rate: ${meshInterval.logRate} not found`,
			);
		}

		return suitableSatInterval;
	}

	return candidateSatInterval;
}

export function getNextTrackingIntervals(
	currentValue: WorkspaceDefaultsFieldsSchema["trackingIntervals"]["value"],
	nextValue: Partial<WorkspaceDefaultsFieldsSchema["trackingIntervals"]["value"]>,
) {
	if (typeof currentValue !== "object" || !("mesh" in currentValue) || !("sat" in currentValue)) {
		throw new Error("Current value is not an object with mesh and sat properties");
	}

	const currentMeshIntervalVal = trackingIntervals.find((t) => t.id === currentValue.mesh);
	const currentSatIntervalVal = trackingIntervals.find((t) => t.id === currentValue.sat);
	if (!currentMeshIntervalVal || !currentSatIntervalVal) {
		throw new Error("Current mesh or sat interval not found");
	}

	const nextVal = Object.entries(nextValue).reduce<Record<string, TrackingInterval>>(
		(acc, [key, value]) => {
			const currentIntervalVal = trackingIntervals.find((t) => t.id === value);
			if (currentIntervalVal) {
				acc[key] = currentIntervalVal;
			}
			return acc;
		},
		{},
	);

	const nextPossibleValues = {
		mesh: currentMeshIntervalVal,
		sat: currentSatIntervalVal,
		...nextVal,
	};

	// Ensure that sat interval is greater or equal to mesh interval
	nextPossibleValues.sat = getCompatibleSatTrackingInterval(
		nextPossibleValues.mesh,
		nextPossibleValues.sat,
	);

	return {
		mesh: nextPossibleValues.mesh.id,
		sat: nextPossibleValues.sat.id,
	};
}

export function getMeshInterval(trackingInterval: number, gpsInterval: number) {
	return meshTrackingIntervals.find(
		(option) => option.shareRate === trackingInterval && option.logRate === gpsInterval,
	);
}

export function getMeshIntervalWithFallback(
	trackingInterval: number,
	gpsInterval: number,
	fallback?: TrackingIntervalIds,
) {
	const meshInterval = getMeshInterval(trackingInterval, gpsInterval);
	if (meshInterval) return meshInterval;

	if (fallback) {
		const fallbackInterval = trackingIntervals.find((t) => t.id === fallback);
		if (!fallbackInterval) {
			return undefined;
		}

		return fallbackInterval;
	}
}

function getSatelliteInterval(
	meshIntervalShareRate: number,
	meshIntervalLogRate: number,
	trackingMultiplier: number,
) {
	return satTrackingIntervals.find(
		(option) =>
			option.shareRate === meshIntervalShareRate * trackingMultiplier &&
			option.logRate === meshIntervalLogRate * trackingMultiplier,
	);
}

export function getCompatibleSatTrackingIntervalFromMultiplier(
	meshIntervalShareRate: number,
	meshIntervalLogRate: number,
	trackingMultiplier: number,
) {
	const satInterval = getSatelliteInterval(
		meshIntervalShareRate,
		meshIntervalLogRate,
		trackingMultiplier,
	);

	if (!satInterval) {
		return;
	}

	return getCompatibleSatTrackingInterval(
		{ shareRate: meshIntervalShareRate, logRate: meshIntervalLogRate },
		satInterval,
	);
}

export function getSplitTrackingIntervals(
	meshTrackingInterval: number,
	gpsInterval: number,
	trackingMultiplier: number,
): SplitIntervalValues {
	const meshInterval = getMeshInterval(meshTrackingInterval, gpsInterval);

	if (!meshInterval) {
		return { mesh: undefined, sat: undefined };
	}

	const satInterval = getCompatibleSatTrackingIntervalFromMultiplier(
		meshInterval.shareRate,
		meshInterval.logRate,
		trackingMultiplier,
	);

	return { mesh: meshInterval.id, sat: satInterval?.id };
}

export function legacyIntervalFromSettings(
	settings: Pick<DeviceSettings.AsObject, "trackingInterval" | "gpsInterval"> | undefined,
	allTrackingIntervals: SelectOption<TrackingInterval>[],
): SelectOption<TrackingInterval> | undefined {
	if (settings === undefined) return undefined;
	return allTrackingIntervals.find(
		(interval) =>
			interval.value.type === IntervalCategory.Legacy &&
			interval.value.shareRate === settings.trackingInterval &&
			interval.value.logRate === settings.gpsInterval,
	);
}

export function getLowBandwidthTrackingMultiplier({
	enabled,
	meshIntervalId,
	satIntervalId,
}: {
	enabled: boolean;
	meshIntervalId: TrackingIntervalIds;
	satIntervalId: TrackingIntervalIds;
}): number {
	if (!enabled) return 0;
	const meshInterval = trackingIntervals.find((t) => t.id === meshIntervalId);
	const satInterval = trackingIntervals.find((t) => t.id === satIntervalId);

	if (!meshInterval || !satInterval) {
		throw new Error("Mesh or sat interval not found");
	}

	const meshIntervalLogRate = meshInterval.logRate;
	const satIntervalLogRate = satInterval.logRate;

	return satIntervalLogRate / meshIntervalLogRate;
}

export function constructNextTrackingIntervalValue(
	fieldValue: SplitIntervalValues | SelectOption<TrackingInterval>,
	key: string,
	val: string,
) {
	if (!("mesh" in fieldValue) && key !== "mesh") {
		// CP - Currently a legacy interval is active, and user pressed a non-mesh option. This
		// should not happen since sat options should be disabled if there is no active mesh split value.
		return;
	}

	const getSplitIntervalValues = () => {
		if ("mesh" in fieldValue) {
			if (!fieldValue.mesh || !fieldValue.sat) {
				return { mesh: val, sat: val };
			}

			return fieldValue;
		}
		return { mesh: val, sat: val };
	};

	const initialMeshValue = getSplitIntervalValues() as {
		mesh: TrackingIntervalIds;
		sat: TrackingIntervalIds;
	};

	if (!initialMeshValue.mesh || !initialMeshValue.sat) {
		return;
	}

	const nextVal = getNextTrackingIntervals(initialMeshValue, {
		[key]: val,
	});

	const meshInterval = meshTrackingIntervals.find((option) => option.id === nextVal.mesh);

	const satInterval = satTrackingIntervals.find((option) => option.id === nextVal.sat);

	if (!meshInterval || !satInterval) {
		return;
	}

	return nextVal;
}

export function doesRadioChannelMatchSettings(
	radioChannel: RadioChannel.AsObject,
	settings: IWorkspaceSettingsState | IOrganizationWorkspaceSettingsState,
) {
	const doesHighSpeedSpreadFactorMatch = () => {
		const channelHighSpeedSpreadFactor =
			radioChannel.highSpeedDataRateParameters?.spreadFactor ??
			RadioSpreadFactor.RADIOSPREADFACTORNONE;
		const settingsHighSpeedSpreadFactor =
			settings.highSpeedDataRateParameters?.spreadFactor ??
			RadioSpreadFactor.RADIOSPREADFACTORNONE;
		return channelHighSpeedSpreadFactor === settingsHighSpeedSpreadFactor;
	};

	const doesHighSpeedBandwidthMatch = () => {
		const channelHighSpeedBandwidth =
			radioChannel.highSpeedDataRateParameters?.bandwidth ??
			RadioBandwidth.RADIOBANDWIDTHNONE;
		const settingsHighSpeedBandwidth =
			settings.highSpeedDataRateParameters?.bandwidth ?? RadioBandwidth.RADIOBANDWIDTHNONE;

		return channelHighSpeedBandwidth === settingsHighSpeedBandwidth;
	};

	return (
		radioChannel.lowSpeedFrequencyHz === settings.lowSpeedFrequencyHz &&
		radioChannel.highSpeedFrequencyHz === settings.highSpeedFrequencyHz &&
		radioChannel.lowSpeedDataRateParameters?.bandwidth ==
			settings.lowSpeedDataRateParameters?.bandwidth &&
		radioChannel.lowSpeedDataRateParameters?.spreadFactor ==
			settings.lowSpeedDataRateParameters?.spreadFactor &&
		doesHighSpeedBandwidthMatch() &&
		doesHighSpeedSpreadFactorMatch()
	);
}
